import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { UpdatedEntryIcon } from '@databases/icons';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { useCallbackEffect, isModifierKeyPressed } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import {
  type BrokenDatabase,
  type Database,
  type EntryReference,
  type EntryWithContext
} from '@databases/types/database';
import { getDatabaseEntryOptional, getEntryName } from '@databases/utils/database/utils';
import { selectBaseUrl } from '@databases/utils/redux/context/selectors';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByIdOptional
} from '@databases/utils/redux/databases/selectors';
import { getEntryPath } from '@databases/utils/urls';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { default as AbstractLink } from './AbstractLink';
import BrokenLink from './BrokenLink';

export interface EntryLinkProps {
  entryRef: EntryReference;
  noFocus?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onFocus?: (e: React.FocusEvent) => void;
  contentId?: string;
  isReadonly?: boolean;
  isHoverCard?: boolean;
}

/**
 * Renders an entry reference. If the entry is unable to be resolved, a broken
 * link is displayed.
 */
const EntryLink: FunctionComponent<EntryLinkProps> = ({
  entryRef,
  noFocus,
  onClick,
  onFocus,
  contentId,
  isReadonly,
  isHoverCard
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const intl = useIntl();
  const { formatMessage } = intl;

  useCallbackEffect(
    useCallback(async () => {
      if (entryRef.contentId) {
        await dispatch(loadDatabaseByContentId(entryRef.contentId));
      }
    }, [dispatch, entryRef.contentId])
  );

  const [targetContentId, href, title] = defaultStoreUtils.useEqualSelector((state) => {
    let database: Database | BrokenDatabase | undefined = undefined;
    if (entryRef.databaseId) {
      database = selectDatabaseByIdOptional(state, {
        spaceKey: entryRef.databaseSpaceKey,
        id: entryRef.databaseId
      });
    } else if (entryRef.contentId) {
      database = selectDatabaseByContentIdOptional(state, {
        contentId: entryRef.contentId
      });
    }

    if (!database) {
      return [undefined, undefined, entryRef.title] as const;
    }

    const databaseDraft =
      database &&
      selectDatabaseDraftByContentId(state, {
        contentId: database.contentId,
        includeDeleted: false
      });

    const entry =
      databaseDraft && getDatabaseEntryOptional(databaseDraft, entryRef.entryId);
    const baseUrl = selectBaseUrl(state);

    const getTitle = (entryWithContext: EntryWithContext) => {
      const entryName = getEntryName(entryWithContext, state);

      return entryName
        ? entryName
        : formatMessage(messages['unnamed-entities.unnamed-entry']);
    };

    return [
      databaseDraft?.contentId,
      entry
        ? `${baseUrl}${getEntryPath({
            contentId: database.contentId,
            entryId: entry.id,
            spaceKey: database.space.key
          })}`
        : undefined,
      entry ? getTitle({ database: databaseDraft, entry }) : entryRef.title
    ] as const;
  });
  const { showPanel } = usePanel();

  const client = useAnalytics();
  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();

      if (contentId) {
        await client.sendEntryLinkClickedEvent({
          contentId: contentId,
          databaseContainer: getDatabaseContainerType()
        });
      }

      if (!isModifierKeyPressed(e) && targetContentId && !isHoverCard) {
        e.preventDefault();
        e.stopPropagation();

        showPanel('EntryViewPanel', {
          entryId: entryRef.entryId,
          contentId: entryRef.contentId,
          noDelete: true,
          isReadonly
        });
      } else {
        onClick?.(e);
      }
    },
    [
      contentId,
      targetContentId,
      client,
      showPanel,
      entryRef.entryId,
      entryRef.contentId,
      isReadonly,
      onClick,
      isHoverCard
    ]
  );

  if (targetContentId && href && title) {
    return (
      <AbstractLink
        href={href}
        onClick={handleClick}
        onFocus={onFocus}
        noFocus={noFocus}
        iconBefore={<UpdatedEntryIcon label="" />}
      >
        {title}
      </AbstractLink>
    );
  } else if (entryRef.entryId) {
    return <BrokenLink />;
  } else {
    return null;
  }
};

export default EntryLink;
